import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ANALYTICS_EVENTS, format, getContent, type RoleModelId } from '@neke/core';
import type { ApiError} from '../api/client';
import { api, type MatchListItem } from '../api/client';
import { useSession } from '../store/session';
import { track } from '../analytics';

const PART_KEYS = ['home', 'kin', 'conditions', 'role'] as const;

export function Matches() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, locale, ready, setUser } = useSession();
  const content = getContent(locale);

  const [matches, setMatches] = useState<MatchListItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!ready) return;
    if (!user) {
      navigate('/login?next=/matches');
      return;
    }
    void api
      .myMatches()
      .then((list) => {
        setMatches(list);
        void track(ANALYTICS_EVENTS.matchesViewed, { count: list.length });
      })
      .catch((e: ApiError) => {
        setError(e.code === 'forbidden' ? 'disabled' : e.message);
        if (e.code === 'forbidden') void track(ANALYTICS_EVENTS.matchesLocked);
      });
  }, [ready, user, navigate]);

  if (error === 'disabled') {
    return (
      <div className="page">
        <div className="card">
          <h2>{t('matches.disabled')}</h2>
          <p className="fine">{t('result.optInLead')}</p>
          <button
            className="btn btn-primary"
            onClick={() => {
              void api
                .updateMe({ optInMatching: true })
                .then((u) => {
                  setUser(u);
                  setError(null);
                  // Само включение подбора считает сервер (`matching_opt_in`):
                  // второй раз отсюда слать не нужно.
                  return api.myMatches().then((list) => {
                    setMatches(list);
                    void track(ANALYTICS_EVENTS.matchesViewed, { count: list.length });
                  });
                })
                .catch((e: ApiError) => setError(e.message));
            }}
          >
            {t('matches.enable')}
          </button>
        </div>
      </div>
    );
  }

  if (error) return <div className="page"><div className="card"><p>{error}</p></div></div>;
  if (!matches) return <div className="page"><p className="muted">{t('common.loading')}</p></div>;

  return (
    <div className="page">
      <div className="stack">
        <h1>{t('matches.title')}</h1>
        <p className="muted">{t('matches.lead')}</p>
      </div>

      {matches.length === 0 ? (
        <p className="notice">{t('matches.empty')}</p>
      ) : (
        matches.map((m) => (
          <div className={`match ${m.tier}`} key={m.candidate.id}>
            <div className="match-head">
              <strong>{m.candidate.name}</strong>
              <span className="fine">
                {[m.candidate.age, m.candidate.city].filter(Boolean).join(', ')}
              </span>
            </div>

            {PART_KEYS.map((key) => (
              <div className="meter" key={key}>
                <span className="meter-label">
                  <span>{key === 'conditions' ? t('result.conditions') : content.axes[key as 'home' | 'kin' | 'role'].name}</span>
                  <b>{Math.round(m.parts[key] ?? 0)}</b>
                </span>
                <i>
                  <em style={{ width: `${Math.min(100, Math.max(2, m.parts[key] ?? 0))}%` }} />
                </i>
              </div>
            ))}

            <p className="fine">
              {content.roleModels[m.roleModel.id as RoleModelId]?.note ?? ''}
            </p>

            {m.reasons.slice(0, 4).map((r, i) => (
              <p className={`reason ${r.tone}`} key={i}>
                {format(content.reasons[r.code as keyof typeof content.reasons] ?? r.code, r.params)}
              </p>
            ))}

            {m.flags.length > 0 && (
              <div className="chips">
                {m.flags.map((f, i) => (
                  <span className={`flag ${f.hard ? 'hard' : ''}`} key={i}>
                    {content.flags[f.code as keyof typeof content.flags] ?? f.code}
                  </span>
                ))}
              </div>
            )}

            {m.candidate.contact && (
              <p className="fine">
                {t('matches.contact')}: <b>{m.candidate.contact}</b>
              </p>
            )}
          </div>
        ))
      )}

      <Link to="/result" className="btn btn-ghost">
        {t('nav.profile')}
      </Link>
    </div>
  );
}
